import React from 'react';
import { type MusicTrack, incrementPlayCount } from '@/services/musicTracksService';
import { TrackCover } from './TrackCover';
import { TrackPlayer } from './TrackPlayer';
import { CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

function formatDuration(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatAddedAt(isoString: string): string {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface TrackRowProps {
  track: MusicTrack;
  isSelected?: boolean;
  onSelect?: () => void;
  activePlayerStoragePath?: string | null;
  onPlayStart?: () => void;
}

export function TrackRow({
  track,
  isSelected = false,
  onSelect,
  activePlayerStoragePath = null,
  onPlayStart,
}: TrackRowProps) {
  const isPlaying = activePlayerStoragePath === track.storagePath;
  const isExcluded = track.excluded ?? false;

  return (
    <div
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect}
      onKeyDown={onSelect ? (e) => e.key === 'Enter' && onSelect() : undefined}
      className={cn(
        'flex items-center gap-4 py-3 px-4 border-b border-border last:border-b-0 transition-colors cursor-pointer',
        'hover:bg-muted/50',
        isSelected && 'bg-muted/70',
        isPlaying && 'border-l-4 border-l-primary bg-primary/5',
        isExcluded && 'opacity-60'
      )}
    >
      <TrackCover
        coverPath={track.coverPath}
        size="md"
        className="shrink-0 h-12 w-12"
        cacheBuster={track.updatedAt}
      />
      <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
        <TrackPlayer
          storagePath={track.storagePath}
          duration={track.duration}
          compact
          lazyLoad
          activeStoragePath={activePlayerStoragePath}
          onPlayStart={onPlayStart}
          trackId={track.id}
          onTrackEnded={(id) => incrementPlayCount(id).catch(() => {})}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{track.title || 'Untitled'}</p>
        <p className="text-sm text-muted-foreground">
          {formatAddedAt(track.createdAt)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <span className="text-xs text-muted-foreground uppercase">
          {track.category}
        </span>
        <div className="flex items-center gap-1.5" title={isExcluded ? 'Excluded from playlist' : 'Included in playlist'}>
          {isExcluded ? (
            <XCircle className="h-4 w-4 text-muted-foreground" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          )}
        </div>
        <span className="text-sm text-muted-foreground tabular-nums w-10 text-right">
          {formatDuration(track.duration)}
        </span>
      </div>
    </div>
  );
}
